import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';

import { brandFormSchema, BrandFormData } from './shared/schema';
import { partnerService } from '@/services/partner';
import { CreatePartnerRequest } from '@/types/partner';
import One from './steps/One';
import Two from './steps/Two';
import Three from './steps/Three';
import Four from './steps/Four';

interface BrandWizardProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  onSuccess: () => void;
}

export default function BrandWizard({ open, onOpenChange, onSuccess }: BrandWizardProps) {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<BrandFormData>({
    mode: 'onChange',
    resolver: zodResolver(brandFormSchema),
    defaultValues: {
      name: '',
      areas: [],
      subsectors: [],
      markets: [],
      regionalization: [],
      subscription: 'BASIC',
      contractStartDate: undefined,
      is_active: true,
      territories: [],
      topics: [],
      contexts: [],
      platforms: [{ link: '', followers: '' }],
      creators: [],
      objectives: [],
      keywords: [],
    }
  });

  const handleNext = async () => {
    const fieldsToValidate = getFieldsForPhase(currentPhase);
    const isValid = await methods.trigger(fieldsToValidate);

    if (currentPhase < 4 && isValid) {
      setCurrentPhase(prev => prev + 1);
    }
  };

  const getFieldsForPhase = (phase: number): (keyof BrandFormData)[] => {
    switch (phase) {
      case 1:
        return ['name', 'areas', 'markets', 'regionalization', 'subscription'];
      case 2:
        return ['territories', 'topics', 'contexts'];
      case 3:
        return ['platforms'];
      case 4:
        return ['creators', 'objectives'];
      default:
        return [];
    }
  };

  const handleBack = () => {
    if (currentPhase > 1) {
      setCurrentPhase(prev => prev - 1);
    }
  };

  const handleSubmit = async (data: BrandFormData) => {
    setIsSubmitting(true);
    
    try {
      const submitData: CreatePartnerRequest = {
        name: data.name,
        creators: data.creators,
        areas: data.areas,
        topics: data.topics,
        territories: data.territories,
        contexts: data.contexts,
        markets: data.markets,
        objectives: data.objectives,
        regionalization: data.regionalization,
        keywords: data.keywords,
        subscription: data.subscription,
        is_active: data.is_active,
        metadata: {
          subsectors: data.subsectors,
          platforms: data.platforms,
          contractStartDate: data.contractStartDate?.toISOString(),
        }
      };

      await partnerService.create(submitData);
      
      toast({
        title: 'Sucesso',
        description: 'Marca cadastrada com sucesso!',
      });
      
      onSuccess();
      onOpenChange(false);
      methods.reset();
      setCurrentPhase(1);
      
    } catch (error) {
      console.error('Erro ao cadastrar marca:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao cadastrar marca. Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentPhase = () => {
    switch (currentPhase) {
      case 1:
        return <One onNext={handleNext} />;
      case 2:
        return <Two onNext={handleNext} onBack={handleBack} />;
      case 3:
        return <Three onNext={handleNext} onBack={handleBack} />;
      case 4:
        return <Four onSubmit={methods.handleSubmit(handleSubmit)} onBack={handleBack} isSubmitting={isSubmitting} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold">
            Cadastro de Marca - Fase {currentPhase} de 4
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...methods}>
          <div className="space-y-4">
            {renderCurrentPhase()}
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
